import { create } from "zustand";
import { combine } from "zustand/middleware";
import type { ActiveFilters } from "@/types/filters.d.ts";
import type { PixelCrop } from "react-image-crop";
import { logApiError, logError } from "@/lib/client/errors";
import { trackEvent } from "@/lib/client/analytics";
import { useFiltersStore } from "./filtersStore";
import { useTokenBudgetStore } from "./tokenBudgetStore";
import { useUiStore } from "./uiStore";
import { tokenService } from "@/lib/client/tokenService";
import { IMPLICIT_EXCLUDED_TYPES } from "@/lib/shared/constants";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";
const IMAGE_SEARCH_ENDPOINT = "/api/v2/vector/image-search";

// Re-export ActiveFilters for convenience
export type { ActiveFilters } from "@/types/filters.d.ts";

type StashedFiltersState = {
  activeFilters: ActiveFilters;
  searchText: string;
  savedFilterId: number | undefined;
  isNewFilter: boolean;
  excludeRelists: boolean;
  excludeAuctions: boolean;
  daysBack: number | undefined;
};

/** Number of items per page for image search */
const IMAGE_SEARCH_PAGE_SIZE = 20;

/** Minimum similarity score for image search results (0-1) */
const IMAGES_SEARCH_THRESHOLD = 0.7;

function getImageSearchErrorCode(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;

  const responseBody = body as Record<string, unknown>;
  if (typeof responseBody.code === "string") return responseBody.code;

  const nestedError = responseBody.error;
  if (nestedError && typeof nestedError === "object") {
    const nestedCode = (nestedError as Record<string, unknown>).code;
    if (typeof nestedCode === "string") return nestedCode;
  }

  return undefined;
}

/** Suppress only the backend's explicit expected unavailable-image contract. */
async function reportImageSearchApiError(response: Response, message: string) {
  const body = typeof response.json === "function" ? await response.json().catch(() => null) : null;
  const code = getImageSearchErrorCode(body);

  if (response.status === 422 && code === "IMAGE_UNAVAILABLE") return;

  logApiError(new Error(`${message}: ${response.status}`), IMAGE_SEARCH_ENDPOINT, {
    status: response.status,
    ...(code && { code }),
  });
}

/**
 * Maps frontend activeFilters to backend Qdrant filter format.
 * Included filters map to Qdrant `must` conditions; excluded filters map to `must_not`.
 *
 * @param activeFilters - Filters to convert, or null
 * @param options - Additional filter options (excludeRelists, excludeAuctions, daysBack, exchangeRates)
 * @returns Filter object for Qdrant API, or null if no filters
 */
function mapFiltersToQdrant(
  activeFilters: ActiveFilters | null,
  options?: {
    excludeRelists?: boolean;
    excludeAuctions?: boolean;
    daysBack?: number;
    exchangeRates?: Record<string, number>;
  }
) {
  const filters: Record<string, unknown> = {};

  if (activeFilters) {
    // Brand IDs
    const includedBrands = activeFilters.brands?.filter((b) => b.state !== "exclude");
    if (includedBrands?.length) {
      const brandIds = includedBrands.map((b) => parseInt(b.value as string, 10));
      filters.brandid = brandIds.length === 1 ? brandIds[0] : brandIds;
    }
    const excludedBrands = activeFilters.brands?.filter((b) => b.state === "exclude");
    if (excludedBrands?.length) {
      const brandIds = excludedBrands.map((b) => parseInt(b.value as string, 10));
      filters.excludedBrandid = brandIds;
    }

    // Item types
    const includedTypes = activeFilters.type?.filter((t) => t.state !== "exclude");
    if (includedTypes?.length) {
      const types = includedTypes.map((t) => t.value as string);
      filters.itemtype = types.length === 1 ? types[0] : types;
    }
    const excludedTypes = activeFilters.type?.filter((t) => t.state === "exclude");
    const excludedTypeValues = excludedTypes?.map((t) => t.value as string) ?? [];
    const allExcludedTypes = [...new Set([...excludedTypeValues, ...IMPLICIT_EXCLUDED_TYPES])];
    filters.excludedItemtype = allExcludedTypes;

    // Vendor names - use label which is the vendor name
    const includedVendors = activeFilters.vendors?.filter((v) => v.state !== "exclude");
    if (includedVendors?.length) {
      const vendors = includedVendors.map((v) => v.value);
      filters.vendorname = vendors.length === 1 ? vendors[0] : vendors;
    }
    const excludedVendors = activeFilters.vendors?.filter((v) => v.state === "exclude");
    if (excludedVendors?.length) {
      filters.excludedVendorname = excludedVendors.map((v) => v.value);
    }

    // Countries - regions in frontend are actually countries
    const includedRegions = activeFilters.regions?.filter((r) => r.state !== "exclude");
    if (includedRegions?.length) {
      const countries = includedRegions.map((r) => r.value as string);
      filters.country = countries.length === 1 ? countries[0] : countries;
    }
    const excludedRegions = activeFilters.regions?.filter((r) => r.state === "exclude");
    if (excludedRegions?.length) {
      filters.excludedCountry = excludedRegions.map((r) => r.value as string);
    }

    // Availability (only included ones)
    const includedAvailability = activeFilters.available?.filter((a) => a.state !== "exclude");
    if (includedAvailability?.length) {
      const availValues = includedAvailability.map((a) => parseInt(a.value as string, 10));
      filters.itemavailability = availValues.length === 1 ? availValues[0] : availValues;
    }

    // Size filters - stored as [{ value: "clothing", size: "M", label: "M" }]
    if (activeFilters.size?.length) {
      filters.sizeFilters = activeFilters.size.map((f) => ({
        type: (f.value as string) || "clothing",
        size: f.size || (f.label as string),
      }));
    }

    // Price range - stored as [{ value: "min,max" }] from DropdownPrice
    if (activeFilters.price?.length > 0) {
      const priceFilter = activeFilters.price[0];
      // Handle Filter object with comma-separated value
      if (priceFilter && typeof priceFilter === "object" && "value" in priceFilter) {
        const priceValue = (priceFilter as { value: string }).value;
        if (typeof priceValue === "string" && priceValue.includes(",")) {
          const [minStr, maxStr] = priceValue.split(",");
          const currency = activeFilters?.currency?.[0]?.value as string | undefined;
          const rate = (currency && options?.exchangeRates?.[currency]) || 1;
          const min = Number(minStr);
          const max = Number(maxStr);
          // Convert from selected currency back to USD (prices stored in USD)
          if (!isNaN(min) && min > 0) {
            filters.minPrice = Math.floor(min / rate);
          }
          if (!isNaN(max) && max < 100000) {
            filters.maxPrice = Math.floor(max / rate);
          }
        }
      }
    }
  }

  // Exclude relists (fkrelist != 0 means it's a relist, so exclude those)
  if (options?.excludeRelists) {
    filters.excludeRelists = true;
  }

  // Exclude auctions (saletype = 2 means it's an auction)
  if (options?.excludeAuctions) {
    filters.excludeAuctions = true;
  }

  // Days back filter (itemtimestamp >= NOW - X days)
  if (options?.daysBack && options.daysBack > 0 && options.daysBack <= 90) {
    filters.daysBack = options.daysBack;
  }

  return Object.keys(filters).length > 0 ? filters : null;
}

// Tracks the in-flight fetch so stale responses can be cancelled
let activeSearchAbortController: AbortController | null = null;

/**
 * Initial state for the image search store.
 * Contains search results, source item, and pagination state.
 */
const initialState = {
  /** Filter state captured before image search entry; restored on exit */
  stashedFiltersState: null as StashedFiltersState | null,
  /** Whether image search mode is active */
  isActive: false,
  /** Set to true when the user clicks the thumbnail to re-crop the current image */
  recropRequested: false,
  /** Original (pre-crop) image URL — kept so re-crop can start from the full image */
  cropSourceUrl: null as string | null,
  /** Pixel crop area from the last confirmed crop — restored when re-cropping */
  lastCropArea: null as PixelCrop | null,
  /** Loading state for initial search */
  loading: false,
  /** Loading state for pagination (load more) */
  loadingMore: false,
  /** Search results (similar items) */
  items: [] as SearchResultItem[],
  /** The source item used for the search */
  sourceItem: null as SearchResultItem | null,
  /** Whether more results are available */
  hasMore: false,
  /** Offset for next page (null if no more) */
  nextOffset: null as number | null,
  /** Total number of results (null if unknown) */
  total: null as number | null,
  /** Optional text search combined with image search */
  searchText: "" as string,
  /** Sort order for image search results */
  sort: "newest" as string,
};

/**
 * Image search store manages vector-based visual search.
 *
 * Features:
 * - Search by image using Qdrant vector similarity
 * - Optional text search combined with image
 * - Filter support (brands, types, vendors, regions, price)
 * - Pagination with load more
 * - Refresh and search with text actions
 *
 * @example
 * ```ts
 * const { actions } = useImageSearchStore();
 * await actions.startSearch(item, activeFilters, "optional text");
 * await actions.loadMore(activeFilters);
 * actions.exit();
 * ```
 */
export const useImageSearchStore = create(
  combine(initialState, (set, get) => ({
    actions: {
      /**
       * Starts a new image search from a source item.
       * Activates search mode, scrolls to top, and fetches first page.
       *
       * @param item - The source item to search with
       * @param activeFilters - Optional filters to apply to results
       * @param text - Optional text to combine with image search
       */
      startSearch: async (
        item: SearchResultItem,
        activeFilters?: ActiveFilters | null,
        text?: string,
        exchangeRates?: Record<string, number>
      ) => {
        activeSearchAbortController?.abort();
        activeSearchAbortController = new AbortController();
        const { signal } = activeSearchAbortController;

        if (!get().isActive) {
          const fs = useFiltersStore.getState();
          set({
            stashedFiltersState: {
              activeFilters: fs.activeFilters,
              searchText: fs.searchText,
              savedFilterId: fs.savedFilterId,
              isNewFilter: fs.isNewFilter,
              excludeRelists: fs.excludeRelists,
              excludeAuctions: fs.excludeAuctions,
              daysBack: fs.daysBack,
            },
          });
          useFiltersStore.getState().actions.resetActiveFilters();
          useFiltersStore.getState().actions.setDaysBack(undefined);
        }

        set({
          isActive: true,
          loading: true,
          loadingMore: false,
          sourceItem: item,
          items: [],
          hasMore: false,
          nextOffset: null,
          total: null,
          searchText: text || "",
          sort: "newest",
        });
        useFiltersStore.getState().actions.setImageSearchActive(true);

        document.getElementById("masonary")?.scrollTo(0, 0);

        try {
          const {
            excludeRelists,
            excludeAuctions,
            daysBack,
            kurboMode,
            activeFilters: currentFilters,
          } = useFiltersStore.getState();

          const currency = (currentFilters?.currency?.[0]?.value as string) || "USD";

          const filters = mapFiltersToQdrant(currentFilters ?? null, {
            excludeRelists,
            excludeAuctions,
            daysBack,
            exchangeRates,
          });

          const clerkToken = await tokenService.getToken();
          // TOKEN_UI_ENABLED is a global kill switch: when it's off, nobody
          // should hit the 24h recency gate, signed in or not. Requiring a
          // clerkToken here (as this used to) left signed-out users gated
          // even with the switch off — token *consumption* accounting for
          // signed-in users is a separate concern and is unaffected.
          const effectiveKurboMode = !TOKEN_UI_ENABLED || kurboMode;
          const res = await fetch(`${API_URL}${IMAGE_SEARCH_ENDPOINT}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Token-Job-Id": crypto.randomUUID(),
              ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
            },
            signal,
            body: JSON.stringify({
              imageUrl: item.imageurl,
              limit: IMAGE_SEARCH_PAGE_SIZE,
              scoreThreshold: IMAGES_SEARCH_THRESHOLD,
              offset: 0,
              currency,
              sort: get().sort,
              kurboMode: effectiveKurboMode,
              ...(filters && { filters }),
              ...(text && { text }),
            }),
          });

          if (res.headers.get("X-Tokens-Used") !== null) {
            useTokenBudgetStore.getState().actions.updateFromHeaders(res.headers);
          }

          if (res.status === 402) {
            const body = await res.json().catch(() => ({}));
            if (body.error === "TOKEN_LIMIT_EXCEEDED") {
              useFiltersStore.getState().actions.setKurboMode(false);
              if (TOKEN_UI_ENABLED) useUiStore.getState().actions.openKurboNoTokensNag();
            }
            set({ loading: false });
            return;
          }

          if (!res.ok) {
            await reportImageSearchApiError(res, "Image search failed");
            set({
              items: [],
              hasMore: false,
              nextOffset: null,
              total: null,
              loading: false,
            });
            return;
          }

          const response = await res.json();
          const data = response?.data || response;
          const items = data?.items || [];
          set({
            items,
            hasMore: data?.hasMore || false,
            nextOffset: data?.nextOffset || null,
            total: data?.total ?? null,
            loading: false,
          });

          // Track image search results for feature effectiveness analysis
          try {
            trackEvent("image_search_result", "search", {
              result_count: items.length,
              total: data?.total ?? null,
              has_results: items.length > 0,
              source_brand: item.brand?.label,
              source_vendor: item.vendor?.label,
              had_text: !!text,
              had_filters: !!activeFilters,
            });
          } catch {
            // Analytics must never affect image search
          }
        } catch (error) {
          if ((error as Error).name === "AbortError") return;
          logError(error as Error, { context: "startSearch" });
          set({
            items: [],
            hasMore: false,
            nextOffset: null,
            total: null,
            loading: false,
          });
        }
      },

      /**
       * Loads more results for the current image search.
       * Uses the nextOffset from the previous search.
       *
       * @param activeFilters - Optional filters to apply to results
       */
      loadMore: async (
        activeFilters?: ActiveFilters | null,
        exchangeRates?: Record<string, number>
      ) => {
        const { sourceItem, nextOffset, loadingMore, hasMore, searchText } = get();
        if (!sourceItem || !hasMore || loadingMore || nextOffset === null) {
          return;
        }

        set({ loadingMore: true });

        try {
          // Get additional filter options from filtersStore
          const { excludeRelists, excludeAuctions, daysBack, kurboMode } =
            useFiltersStore.getState();

          const currency = (activeFilters?.currency?.[0]?.value as string) || "USD";

          const filters = mapFiltersToQdrant(activeFilters ?? null, {
            excludeRelists,
            excludeAuctions,
            daysBack,
            exchangeRates,
          });

          const clerkToken = await tokenService.getToken();
          // TOKEN_UI_ENABLED is a global kill switch: when it's off, nobody
          // should hit the 24h recency gate, signed in or not. Requiring a
          // clerkToken here (as this used to) left signed-out users gated
          // even with the switch off — token *consumption* accounting for
          // signed-in users is a separate concern and is unaffected.
          const effectiveKurboMode = !TOKEN_UI_ENABLED || kurboMode;
          const res = await fetch(`${API_URL}${IMAGE_SEARCH_ENDPOINT}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Token-Job-Id": crypto.randomUUID(),
              ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
            },
            signal: activeSearchAbortController?.signal,
            body: JSON.stringify({
              imageUrl: sourceItem.imageurl,
              limit: IMAGE_SEARCH_PAGE_SIZE,
              scoreThreshold: IMAGES_SEARCH_THRESHOLD,
              offset: nextOffset,
              currency,
              sort: get().sort,
              kurboMode: effectiveKurboMode,
              ...(filters && { filters }),
              ...(searchText && { text: searchText }),
            }),
          });

          if (res.headers.get("X-Tokens-Used") !== null) {
            useTokenBudgetStore.getState().actions.updateFromHeaders(res.headers);
          }

          if (res.status === 402) {
            const body = await res.json().catch(() => ({}));
            if (body.error === "TOKEN_LIMIT_EXCEEDED") {
              useFiltersStore.getState().actions.setKurboMode(false);
              if (TOKEN_UI_ENABLED) useUiStore.getState().actions.openKurboNoTokensNag();
            }
            set({ loadingMore: false });
            return;
          }

          if (!res.ok) {
            await reportImageSearchApiError(res, "Image search load more failed");
            set({ loadingMore: false });
            return;
          }

          const response = await res.json();
          const data = response?.data || response;
          const newItems = data?.items || [];
          const currentItems = get().items;

          set({
            items: [...currentItems, ...newItems],
            hasMore: data?.hasMore || false,
            nextOffset: data?.nextOffset || null,
            loadingMore: false,
          });
        } catch (error) {
          if ((error as Error).name === "AbortError") return;
          logError(error as Error, { context: "loadMore" });
          set({ loadingMore: false });
        }
      },

      /**
       * Refreshes the current image search from page 1.
       * Keeps the same source item and search text, resets pagination.
       *
       * @param activeFilters - Optional filters to apply to results
       */
      refresh: async (
        activeFilters?: ActiveFilters | null,
        exchangeRates?: Record<string, number>
      ) => {
        const { sourceItem, searchText } = get();
        if (!sourceItem) return;

        activeSearchAbortController?.abort();
        activeSearchAbortController = new AbortController();
        const { signal } = activeSearchAbortController;

        set({
          loading: true,
          loadingMore: false,
          items: [],
          hasMore: false,
          nextOffset: null,
          total: null,
        });

        try {
          // Get additional filter options from filtersStore
          const { excludeRelists, excludeAuctions, daysBack, kurboMode } =
            useFiltersStore.getState();

          const currency = (activeFilters?.currency?.[0]?.value as string) || "USD";

          const filters = mapFiltersToQdrant(activeFilters ?? null, {
            excludeRelists,
            excludeAuctions,
            daysBack,
            exchangeRates,
          });

          const clerkToken = await tokenService.getToken();
          // TOKEN_UI_ENABLED is a global kill switch: when it's off, nobody
          // should hit the 24h recency gate, signed in or not. Requiring a
          // clerkToken here (as this used to) left signed-out users gated
          // even with the switch off — token *consumption* accounting for
          // signed-in users is a separate concern and is unaffected.
          const effectiveKurboMode = !TOKEN_UI_ENABLED || kurboMode;
          const res = await fetch(`${API_URL}${IMAGE_SEARCH_ENDPOINT}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Token-Job-Id": crypto.randomUUID(),
              ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
            },
            signal,
            body: JSON.stringify({
              imageUrl: sourceItem.imageurl,
              limit: IMAGE_SEARCH_PAGE_SIZE,
              scoreThreshold: IMAGES_SEARCH_THRESHOLD,
              offset: 0,
              currency,
              sort: get().sort,
              kurboMode: effectiveKurboMode,
              ...(filters && { filters }),
              ...(searchText && { text: searchText }),
            }),
          });

          if (res.headers.get("X-Tokens-Used") !== null) {
            useTokenBudgetStore.getState().actions.updateFromHeaders(res.headers);
          }

          if (res.status === 402) {
            const body = await res.json().catch(() => ({}));
            if (body.error === "TOKEN_LIMIT_EXCEEDED") {
              useFiltersStore.getState().actions.setKurboMode(false);
              if (TOKEN_UI_ENABLED) useUiStore.getState().actions.openKurboNoTokensNag();
            }
            set({ loading: false });
            return;
          }

          if (!res.ok) {
            await reportImageSearchApiError(res, "Image search refresh failed");
            set({ loading: false });
            return;
          }

          const response = await res.json();
          const data = response?.data || response;
          const items = data?.items || [];
          set({
            items,
            hasMore: data?.hasMore || false,
            nextOffset: data?.nextOffset || null,
            total: data?.total ?? null,
            loading: false,
          });
        } catch (error) {
          if ((error as Error).name === "AbortError") return;
          logError(error as Error, { context: "refresh" });
          set({ hasMore: false, nextOffset: null, total: null, loading: false });
        }
      },

      /**
       * Performs a new search with updated text.
       * Keeps the same source item but updates the search text.
       *
       * @param text - New text to search with
       * @param activeFilters - Optional filters to apply to results
       */
      searchWithText: async (
        text: string,
        activeFilters?: ActiveFilters | null,
        exchangeRates?: Record<string, number>
      ) => {
        const sourceItem = get().sourceItem;
        if (!sourceItem) return;

        activeSearchAbortController?.abort();
        activeSearchAbortController = new AbortController();
        const { signal } = activeSearchAbortController;

        set({
          loading: true,
          loadingMore: false,
          items: [],
          hasMore: false,
          nextOffset: null,
          total: null,
          searchText: text,
        });

        // Scroll to top
        document.getElementById("masonary")?.scrollTo(0, 0);

        try {
          // Get additional filter options from filtersStore
          const { excludeRelists, excludeAuctions, daysBack, kurboMode } =
            useFiltersStore.getState();

          const currency = (activeFilters?.currency?.[0]?.value as string) || "USD";

          const filters = mapFiltersToQdrant(activeFilters ?? null, {
            excludeRelists,
            excludeAuctions,
            daysBack,
            exchangeRates,
          });

          const clerkToken = await tokenService.getToken();
          // TOKEN_UI_ENABLED is a global kill switch: when it's off, nobody
          // should hit the 24h recency gate, signed in or not. Requiring a
          // clerkToken here (as this used to) left signed-out users gated
          // even with the switch off — token *consumption* accounting for
          // signed-in users is a separate concern and is unaffected.
          const effectiveKurboMode = !TOKEN_UI_ENABLED || kurboMode;
          const res = await fetch(`${API_URL}${IMAGE_SEARCH_ENDPOINT}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Token-Job-Id": crypto.randomUUID(),
              ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
            },
            signal,
            body: JSON.stringify({
              imageUrl: sourceItem.imageurl,
              limit: IMAGE_SEARCH_PAGE_SIZE,
              scoreThreshold: IMAGES_SEARCH_THRESHOLD,
              offset: 0,
              currency,
              sort: get().sort,
              kurboMode: effectiveKurboMode,
              ...(filters && { filters }),
              ...(text && { text }),
            }),
          });

          if (res.headers.get("X-Tokens-Used") !== null) {
            useTokenBudgetStore.getState().actions.updateFromHeaders(res.headers);
          }

          if (res.status === 402) {
            const body = await res.json().catch(() => ({}));
            if (body.error === "TOKEN_LIMIT_EXCEEDED") {
              useFiltersStore.getState().actions.setKurboMode(false);
              if (TOKEN_UI_ENABLED) useUiStore.getState().actions.openKurboNoTokensNag();
            }
            set({ loading: false });
            return;
          }

          if (!res.ok) {
            await reportImageSearchApiError(res, "Image search with text failed");
            set({ loading: false });
            return;
          }

          const response = await res.json();
          const data = response?.data || response;
          const items = data?.items || [];
          set({
            items,
            hasMore: data?.hasMore || false,
            nextOffset: data?.nextOffset || null,
            total: data?.total ?? null,
            loading: false,
          });
        } catch (error) {
          if ((error as Error).name === "AbortError") return;
          logError(error as Error, { context: "searchWithText" });
          set({ hasMore: false, nextOffset: null, total: null, loading: false });
        }
      },

      /** Updates the sort order for image search results */
      setSort: (sort: string) => set({ sort }),

      /**
       * Clears the source item.
       * Use this when leaving image search mode but keeping results.
       */
      clearSource: () => set({ sourceItem: null, searchText: "", total: null }),

      /**
       * Exits image search mode and resets all state.
       * Call this when navigating away from image search.
       */
      exit: () => {
        activeSearchAbortController?.abort();
        activeSearchAbortController = null;
        const stashed = get().stashedFiltersState;
        set(initialState);
        useFiltersStore.getState().actions.setImageSearchActive(false);
        if (stashed) {
          useFiltersStore.setState({
            activeFilters: stashed.activeFilters,
            searchText: stashed.searchText,
            savedFilterId: stashed.savedFilterId,
            isNewFilter: stashed.isNewFilter,
            excludeRelists: stashed.excludeRelists,
            excludeAuctions: stashed.excludeAuctions,
            daysBack: stashed.daysBack,
          });
        }
      },

      /** Resets the store to initial state (alias for exit) */
      reset: () => {
        activeSearchAbortController?.abort();
        activeSearchAbortController = null;
        const stashed = get().stashedFiltersState;
        set(initialState);
        useFiltersStore.getState().actions.setImageSearchActive(false);
        if (stashed) {
          useFiltersStore.setState({
            activeFilters: stashed.activeFilters,
            searchText: stashed.searchText,
            savedFilterId: stashed.savedFilterId,
            isNewFilter: stashed.isNewFilter,
            excludeRelists: stashed.excludeRelists,
            excludeAuctions: stashed.excludeAuctions,
            daysBack: stashed.daysBack,
          });
        }
      },

      /** Signals ImageUploadZone to open the crop UI for the current source image */
      requestRecrop: () => set({ recropRequested: true }),
      clearRecropRequest: () => set({ recropRequested: false }),
      /** Saves the original image and crop area so re-crop can restore them */
      saveCropSource: (url: string, area: PixelCrop) =>
        set({ cropSourceUrl: url, lastCropArea: area }),
    },
  }))
);
